import { C } from "../colors.js";
import { resolutionMomentLocal } from "../formatDates.js";
import { isVoidedContract } from "./contractStatus.js";

// Which resolved OUTCOME to show for a forecast card / settings panel (or null).
// Mirrors the timeline's showVerdictVisual so the card + panel NEVER diverge from
// the timeline: a terminal VERDICT (resolved_yes / resolved_no / resolved_tie), an
// "unresolved" verdict once past the resolution date, or the debug override.
// Deliberately NOT `contractStatus === "resolved"` alone — a contract can be
// policy-resolved BEFORE the resolver stamps a verdict (contractStatus="resolved"
// with resolution=null), and treating that as UNRESOLVED (while the timeline shows
// nothing) is the exact bug this fixes. `contractStatus === "voided"` IS consulted,
// because a void is the one terminal state that carries no verdict of its own — read
// through the verdict alone it is indistinguishable from "the resolver never ran".
// `override` is the debug Force-Resolution value. Returns
// `{ label, color, namesOutcome }` or null — see `namesOutcome` at the return below.
export function resolvedOutcome(f, nowMs, override = null) {
  const status = override || f?.resolution?.status;
  // A VOID short-circuits the date test below: it is terminal when stamped, not once a
  // date passes. Checked before the verdict branch so the status wins if a row ever
  // carries both — claiming an outcome for a question that can never resolve is the
  // worse error. The debug override outranks both.
  if (!override && isVoidedContract(f))
    return { label: "VOIDED", color: C.gray, namesOutcome: false };
  const terminal =
    status === "resolved_yes" || status === "resolved_no" || status === "resolved_tie";
  let show = terminal;
  if (!show) {
    // Any NON-terminal state surfaces UNRESOLVED once past the resolution line —
    // whether the resolver ran and returned "unresolved" OR never ran at all (status
    // null/undefined). Once the resolution moment is past, the card must NEVER keep a
    // live % (mirrors the timeline's effectiveUnresolved). Before the moment it stays
    // null → the forecast still reads as active. The debug override forces it for
    // previewing.
    if (override === "unresolved") show = true;
    else {
      const d = f?.contract?.resolution_date
        ? resolutionMomentLocal(f.contract.resolution_date, f?.question)
        : null;
      // App threads effectiveToday as nowMs; fall back to wall-clock so the
      // helper stays correct if a caller omits it (else `< undefined` is always
      // false and a past-date unresolved verdict would never surface).
      const now = Number.isFinite(nowMs) ? nowMs : Date.now();
      show = !!d && !isNaN(d.getTime()) && d.getTime() < now;
    }
  }
  if (!show) return null;
  // Labels/colors match the timeline verdict + Report modal (a tie is distinct
  // from unresolved — "TIED"/purple, not "UNRESOLVED"/gray).
  //
  // `namesOutcome` says whether the label states WHAT HAPPENED, which is what earns a
  // row the settled treatment — the accent colour and the check. Read it; never infer
  // settledness from the label text, which cannot distinguish the two that name nothing.
  return status === "resolved_yes"
    ? { label: "OCCURRED", color: C.blueTeam, namesOutcome: true }
    : status === "resolved_no"
      ? { label: "DID NOT OCCUR", color: C.redTeam, namesOutcome: true }
      : status === "resolved_tie"
        ? { label: "TIED", color: C.purpleL, namesOutcome: true }
        : { label: "UNRESOLVED", color: C.gray, namesOutcome: false };
}
